/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// Component imports
import { Button, Tooltip } from 'antd';
import { StarFilled, StarOutlined } from '@ant-design/icons';

// Misc imports
import { PERMISSIONS_GROUP_EDITOR } from '../../constants/main';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import { useFetchMainChapterSourceQuery, useUpdateMainChapterSourceMutation } from '../../store/chapterSources/chapterSourcesApi';

const MainActivityButton = ({
  chaptersource,
  className,
  buttonType = 'default',
  disabled,
  hideIfNotMain = false,
  customIndicator = null, // Custom indicator to show instead of the star icon button
  botId = null
}) => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const userPermissions = useUserPermissions(orgId, botId);

  const { data: mainChaptersource, isFetching: isFetchingMainChaptersource } = useFetchMainChapterSourceQuery({ orgId, tenantId: botId }, { skip: !botId });
  const [updateMainChapterSource, { isLoading: isUpdatingMainChaptersource }] = useUpdateMainChapterSourceMutation();

  const renderTitle = () => {
    return chaptersource.id === mainChaptersource?.id ? t('Remove main from this flow.') : t('Mark this flow as main. The main flow is used as the default when previewing the Flow.');
  };

  const updateMainActivity = async () => {
    await updateMainChapterSource({ orgId, tenantId: botId, data: { id: chaptersource.id === mainChaptersource?.id ? null : chaptersource.id } }).unwrap();
  };

  const renderButton = () => {
    if (hideIfNotMain && chaptersource.id !== mainChaptersource?.id) {
      return null;
    }

    // If custom indicator is provided
    if (customIndicator) {
      return customIndicator;
    }

    // Else
    return (
      <Tooltip
        overlayClassName="pointer-events-none"
        placement="topRight"
        title={!disabled && renderTitle()}
      >
        <Button
          type={buttonType}
          shape="circle"
          icon={chaptersource.id === mainChaptersource?.id ? <StarFilled className="text-warning" /> : <StarOutlined />}
          onClick={() => updateMainActivity()}
          disabled={disabled}
          loading={isFetchingMainChaptersource || isUpdatingMainChaptersource}
          className={classNames('text-warning', className, {
            'pointer-events-none': !userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission))
          })}
        />
      </Tooltip>
    );
  };

  return renderButton();
};

MainActivityButton.propTypes = {
  chaptersource: PropTypes.object,
  className: PropTypes.string,
  buttonType: PropTypes.string,
  disabled: PropTypes.bool,
  hideIfNotMain: PropTypes.bool,
  customIndicator: PropTypes.node,
  botId: PropTypes.string
};

export default MainActivityButton;
